/**
 * When the analysis pane keeps what it is showing, and when it must not.
 *
 * Three decisions, pulled out of `main.ts` because they are the kind that are
 * wrong silently: each one is a conditional whose failure mode is a page that
 * renders fine and says something untrue.
 *
 * The rule underneath all three: **results are shown under the tensor they were
 * computed for, and nowhere else.** A sweep that adds a panel does not
 * invalidate them; a change of tensor does, completely.
 */

/**
 * Does a re-analysis keep the results already on screen?
 *
 * Only when the subject is unchanged. The what-if sweep is eighteen passes over
 * the weights and the clip grid forty-eight — seconds on a large tensor — and
 * blanking for that long discarded a histogram that was still exactly true and
 * was not being recomputed (owner, 2026-08-10). The visitor asked for an
 * *additional* panel, so the answer they already have stays up.
 *
 * A different tensor is the opposite case and gets the opposite answer: leaving
 * the old histogram under a new tensor's title is the wrong-subject failure this
 * codebase has met five times, and it would look entirely normal.
 */
export function keepsResultsWhileWorking(
  renderedFor: string | undefined,
  rendered: string | undefined,
  nextTensor: string,
): boolean {
  return Boolean(rendered) && renderedFor === nextTensor;
}

/**
 * The rendered analysis, if it belongs to the current selection.
 *
 * Compare mode's diff click-through changes the selection without touching the
 * analysis, so returning to the single view could paint the previous tensor's
 * results under the new one's title until the next read finished. One
 * comparison closes it.
 */
export function analysisForSelection(
  renderedFor: string | undefined,
  selected: string | undefined,
  rendered: string | undefined,
): string | undefined {
  if (renderedFor === undefined || selected === undefined) return undefined;
  return renderedFor === selected ? rendered : undefined;
}

/**
 * What the banner says while a sweep runs over results that stay visible.
 *
 * It names the sweep and states that nothing below is changing, because a
 * spinner above unchanged content otherwise reads as "these numbers are being
 * replaced" — which is the thing the banner exists to deny.
 */
export function busyMessage(options: {
  readonly whatIf: boolean;
  readonly clipGrid: boolean;
}): string {
  const unchanged = "the results below are unchanged while this runs.";
  if (options.whatIf) {
    return `Simulating quantization across the bits × group grid — ${unchanged}`;
  }
  if (options.clipGrid) return `Sweeping the clip grid — ${unchanged}`;
  return `Re-reading — ${unchanged}`;
}
